import { inject, Injectable, signal } from "@angular/core";
import { CustomFormArray, CustomFormControl, CustomFormControlProps, CustomFormGroup } from "@tilby/tilby-ui-lib/components/tilby-magic-form";
import { GENERAL_FE_KEYS, ItemGeneralCostFormGroupFields, ItemGeneralFormGroups, ItemGeneralValidators, ItemPrintersFields, ItemsFormWithCustomPrinters, ItemsPrintersFormArray } from "./item-general.model";
import { ItemDefault, ItemFields, ItemsForm } from "src/app/features/items/models/items-form.model";
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { ItemDataCatalogService } from "src/app/features/items/services/item-data-catalog.service";
import { SupplierCodes } from "src/app/features/items/utils/item.interface";
import { Items, ItemsPrinters, Printers, Suppliers } from "tilby-models";
import { REGEX } from "src/app/core/constants/regex";
import { color_items } from "src/app/features/items/utils/item.constants";
import { TranslateService } from "@ngx-translate/core";


const RequireMatch = <T extends { code: string, name: string }>(controlList: T[]): ValidatorFn => {
    return (control: AbstractControl): ValidationErrors|null => {
        const selection: any = control.value?.toString() || '';
        if (controlList.map(({name}) => name).indexOf(selection) > -1 ||
         controlList.map(({code}) => code).indexOf(selection) > -1 || selection === '' ) {
            return null;
        }
        return {matchRequired: true};
    }
}
@Injectable()
export class ItemGeneralFormService {

    private readonly itemDataCatalogService = inject(ItemDataCatalogService);
    private readonly translateService = inject(TranslateService);

    private itemGeneralForm!: CustomFormGroup<ItemGeneralFormGroups>;

    get form() {
        return this.itemGeneralForm;
    }

    supplierCodes: Array<SupplierCodes> = new Array<SupplierCodes>();
    printersByCategory: Map<number, Printers[]> = new Map();
    
    itemGeneralFormReady = signal(false);
    itemDataGeneralFormReady = signal(false);
    
    validators: ItemGeneralValidators = {
        nameMaxLength: 255,
        seasonMaxLength: 255,
        brandMaxLength: 255,
        indexMin: 0,
        pricesMin: 0,
        costNoVatMin: 0,
        skuMaxLength: 64,
        codeMaxLength: 255,
        weightMin: 0,
        codePienissimoMaxLength: 255,

        // --------
        provMinLength: 0,
        provMaxLength: 255,
        provMaxLengthArray: new Array(10).fill(2)
    };

    createForm(item: ItemFields = new ItemDefault(),hiddenFields?:Array<keyof ItemFields>) {
        this.supplierCodes = this.getSupplierCodes();
        this.printersByCategory = this.itemDataCatalogService.getPrintersByCategory() || new Map();

        this.itemGeneralForm = new CustomFormGroup<ItemGeneralFormGroups>({
            detailsForm: new CustomFormGroup<ItemsForm>({
                on_sale: new CustomFormControl(
                    item.on_sale
                ),
                not_discountable: new CustomFormControl(
                    item.not_discountable
                ),
                favorite: new CustomFormControl(
                    item.favorite
                ),
                name: new CustomFormControl(
                    item.name || null, 
                    { validators: [Validators.required, Validators.maxLength(this.validators.nameMaxLength)] }, 
                    {
                        ...new CustomFormControlProps(),
                        hint: () => `${this.name?.value?.length || 0}/ ${this.validators.nameMaxLength}`
                    }
                ),
                alternative_names: new CustomFormControl(
                    (item.alternative_names || []).map(alt => ({...alt}))
                ),
                category: new CustomFormControl(
                    item.category?.id ?? 0,
                    {}, 
                    {
                        ...new CustomFormControlProps(),
                        inputType: 'select',
                        inputChoices: this.itemDataCatalogService.getExtendedCategories()?.map(({ id, name }) => ({ value: `${id}`, key: name })) || []
                    }
                ),
                color: new CustomFormControl(
                    item.color || null,
                    {}, 
                    {
                        ...new CustomFormControlProps(),
                        inputType: 'select',
                        inputChoices: color_items?.map(({ value, name }) => ({ value: `${value}`, key: name })) || []
                    }
                ),
                index: new CustomFormControl(
                    item.index || null, 
                    { validators: [Validators.min(this.validators.indexMin)] }, 
                    {
                        ...new CustomFormControlProps(),
                        inputType: 'number'
                    }
                ),
                barcodes: new CustomFormControl(
                    item.barcodes || []
                ),
                season: new CustomFormControl(
                    item.season || null, 
                    { validators: [Validators.maxLength(this.validators.seasonMaxLength)] }, 
                ),
                brand: new CustomFormControl(
                    item.brand || null, 
                    { validators: [Validators.maxLength(this.validators.brandMaxLength)] }, 
                ),
                default_supplier_id: new CustomFormControl(
                    item.default_supplier_id || null,
                    { validators: RequireMatch(this.supplierCodes) },
                    {
                        ...new CustomFormControlProps(),
                        inputType: 'autocomplete',
                        inputChoices: this.supplierCodes.map(({ code, name }) => ({ key: code, value: name }))
                    }
                ),
                keyboard_code: new CustomFormControl(
                    item.keyboard_code || null, 
                    { validators: [Validators.pattern(REGEX.lettersAndNumbersOnly)] }
                ),
                request_weighing: new CustomFormControl(
                    item.request_weighing
                ),
                unit: new CustomFormControl(
                    item.unit || null,
                    {}, 
                    {
                        ...new CustomFormControlProps(),
                        inputType: 'select'
                    }
                ),
                sku: new CustomFormControl(
                    item.sku || null, 
                    { validators: [Validators.maxLength(this.validators.skuMaxLength)] }, 
                ),
                code: new CustomFormControl(
                    item.code || null, 
                    { validators: [Validators.maxLength(this.validators.codeMaxLength)] }, 
                ),
                weight: new CustomFormControl(
                    item.weight || null, 
                    { validators: [Validators.min(this.validators.weightMin)] }, 
                    {
                        ...new CustomFormControlProps(),
                        inputType: 'number'
                    }
                ),
                code_pienissimo: new CustomFormControl(
                    item.code_pienissimo || null,
                    { validators: [Validators.maxLength(this.validators.codePienissimoMaxLength)] }, 

                )
            }),
            pricesForm: new CustomFormGroup<ItemsForm>({
                price1: new CustomFormControl(
                    item.price1 != null? item.price1 : null, 
                    { validators: [Validators.required, Validators.min(this.validators.pricesMin)] }, 
                    {
                        ...new CustomFormControlProps(),
                        inputType: 'number'
                    }
                ),
                department_id: new CustomFormControl(
                    item.department_id || null,
                    { validators: [Validators.required] },
                    {
                        ...new CustomFormControlProps(),
                        inputType: 'select',
                    }
                ),
                price2: new CustomFormControl(
                    item.price2 != null? item.price2 : null, 
                    { validators: [Validators.min(this.validators.pricesMin)] }, 
                    {
                        ...new CustomFormControlProps(),
                        inputType: 'number'
                    }
                ),
                department2_id: new CustomFormControl(
                    item.department2_id || null,
                    {}, 
                    {
                        ...new CustomFormControlProps(),
                        inputType: 'select',
                    }
                ),
                price3: new CustomFormControl(
                    item.price3 != null? item.price3 : null, 
                    { validators: [Validators.min(this.validators.pricesMin)] }, 
                    {
                        ...new CustomFormControlProps(),
                        inputType: 'number'
                    }
                ),
                department3_id: new CustomFormControl(
                    item.department3_id || null,
                    {}, 
                    {
                        ...new CustomFormControlProps(),
                        inputType: 'select',
                    }
                ),
                price4: new CustomFormControl(
                    item.price4 != null? item.price4 : null, 
                    { validators: [Validators.min(this.validators.pricesMin)] }, 
                    {
                        ...new CustomFormControlProps(),
                        inputType: 'number'
                    }
                ),
                department4_id: new CustomFormControl(
                    item.department4_id || null,
                    {}, 
                    {
                        ...new CustomFormControlProps(),
                        inputType: 'select',
                    }
                ),
                price5: new CustomFormControl(
                    item.price5 != null? item.price5 : null,
                    { validators: [Validators.min(this.validators.pricesMin)] }, 
                    {
                        ...new CustomFormControlProps(),
                        inputType: 'number'
                    }
                ),
                department5_id: new CustomFormControl(
                    item.department5_id || null,
                    {}, 
                    {
                        ...new CustomFormControlProps(),
                        inputType: 'select',
                    }
                ),
                price6: new CustomFormControl(
                    item.price6 != null? item.price6 : null, 
                    { validators: [Validators.min(this.validators.pricesMin)] }, 
                    {
                        ...new CustomFormControlProps(),
                        inputType: 'number'
                    }
                ),
                department6_id: new CustomFormControl(
                    item.department6_id || null,
                    {}, 
                    {
                        ...new CustomFormControlProps(),
                        inputType: 'select',
                    }
                ),
                price7: new CustomFormControl(
                    item.price7 != null? item.price7 : null, 
                    { validators: [Validators.min(this.validators.pricesMin)] }, 
                    {
                        ...new CustomFormControlProps(),
                        inputType: 'number'
                    }
                ),
                department7_id: new CustomFormControl(
                    item.department7_id || null,
                    {}, 
                    {
                        ...new CustomFormControlProps(),
                        inputType: 'select',
                    }
                ),
                price8: new CustomFormControl(
                    item.price8 != null? item.price8 : null,
                    { validators: [Validators.min(this.validators.pricesMin)] }, 
                    {
                        ...new CustomFormControlProps(),
                        inputType: 'number'
                    }
                ),
                department8_id: new CustomFormControl(
                    item.department8_id || null,
                    {}, 
                    {
                        ...new CustomFormControlProps(),
                        inputType: 'select',
                    }
                ),
                price9: new CustomFormControl(
                    item.price9 != null? item.price9 : null, 
                    { validators: [Validators.min(this.validators.pricesMin)] }, 
                    {
                        ...new CustomFormControlProps(),
                        inputType: 'number'
                    }
                ),
                department9_id: new CustomFormControl(
                    item.department9_id || null,
                    {}, 
                    {
                        ...new CustomFormControlProps(),
                        inputType: 'select',
                    }
                ),
                price10: new CustomFormControl(
                    item.price10 != null? item.price10 : null,
                    { validators: [Validators.min(this.validators.pricesMin)] }, 
                    {
                        ...new CustomFormControlProps(),
                        inputType: 'number'
                    }
                ),
                department10_id: new CustomFormControl(
                    item.department10_id || null,
                    {}, 
                    {
                        ...new CustomFormControlProps(),
                        inputType: 'select',
                    }
                )
            }),
            costsForm: new CustomFormGroup<ItemGeneralCostFormGroupFields>({
                cost: new CustomFormControl(
                    item.cost || null, 
                    { validators: [Validators.min(this.validators.costNoVatMin)] }, 
                    {
                        ...new CustomFormControlProps(),
                        inputType: 'number'
                    }
                ),
                itemMargin: new CustomFormControl(null), 
                itemMarkup: new CustomFormControl(null)
            }),
            printersForm: new CustomFormGroup<ItemsFormWithCustomPrinters>({
                printers: new CustomFormArray<CustomFormGroup<ItemsPrintersFormArray>>(
                    []
                )
            })
        })

        this.createPrinters();
        if (item.printers?.length) this.initPrinters(item.printers);

        this.itemGeneralFormReady.set(true);
        return this.itemGeneralForm;
    }

    private createPrinters() {
        const printersList: Printers[] = this.itemDataCatalogService.getDataByKey("non-fiscal-printers") || [];
        printersList.forEach(printer => {
            this.printersArray?.push(new CustomFormGroup<ItemsPrintersFormArray>({
                id: new CustomFormControl(
                    printer.id || null
                ),
                name: new CustomFormControl(
                    printer.name || null
                ),
                _enabled: new CustomFormControl(
                    false
                ),
                _categoryEnabled: new CustomFormControl(
                    false
                )
            }))
        })
    }

    private initPrinters(printers: ItemsPrinters[]) {
        printers.forEach(printer => {
            const printerIndex = this.printersArray?.value.findIndex(p => p.id === printer.id);
            if(printerIndex != undefined && printerIndex >= 0) {
                this.getEnabledPrinter(printerIndex)?.setValue(true);
            }
        })
    }

    resetFormValues(item: Items = new ItemDefault()) {
        this.itemGeneralForm.patchValue({
            detailsForm: {
                on_sale: item.on_sale,
                not_discountable: item.not_discountable,
                favorite: item.favorite,
                name: item.name || null,
                alternative_names: (item.alternative_names || []).map(alt => ({...alt})),
                category: item.category?.id ?? 0,
                color: item.color || null,
                index: item.index || null,
                barcodes: item.barcodes || [],
                season: item.season || null,
                brand: item.brand || null,
                default_supplier_id: item.default_supplier_id || null,
                keyboard_code: item.keyboard_code || null,
                request_weighing: item.request_weighing,
                unit: item.unit || null,
                sku: item.sku || null,
                code: item.code || null,
                weight: item.weight || null,
                code_pienissimo: item.code_pienissimo || null
            },
            pricesForm: {
                price1: item.price1 != null ? item.price1 : null,
                department_id: item.department_id || null,
                price2: item.price2 != null ? item.price2 : null,
                department2_id: item.department2_id || null,
                price3: item.price3 != null ? item.price3 : null,
                department3_id: item.department3_id || null,
                price4: item.price4 != null ? item.price4 : null,
                department4_id: item.department4_id || null,
                price5: item.price5 != null ? item.price5 : null,
                department5_id: item.department5_id || null,
                price6: item.price6 != null ? item.price6 : null,
                department6_id: item.department6_id || null,
                price7: item.price7 != null ? item.price7 : null,
                department7_id: item.department7_id || null,
                price8: item.price8 != null ? item.price8 : null,
                department8_id: item.department8_id || null,
                price9: item.price9 != null ? item.price9 : null,
                department9_id: item.department9_id || null,
                price10: item.price10 != null ? item.price10 : null,
                department10_id: item.department10_id || null
            },
            costsForm: {
                cost: item.cost || null,
                itemMargin: null,
                itemMarkup: null
            }
        });

        this.printersArray?.controls.forEach(control => {
            control.get('_enabled')?.setValue(false);
        })

        if (item.printers?.length) this.initPrinters(item.printers);
        this.itemGeneralForm.markAsPristine();

        if(item.stock_type && item.stock_type === 'bom') {
            this.cost?.disable();
        } else this.cost?.enable();
    }

    getSupplierCodes(): SupplierCodes[] {
        const suppliersList = this.itemDataCatalogService.getDataByKey("suppliers");
        let supplierCodes: SupplierCodes[] = [];
        if(suppliersList) {
            supplierCodes = suppliersList.map((sup: Suppliers) => ({ code: sup.id? sup.id.toString(): '', name: sup.name }));
        }
        return supplierCodes;
    }

    updateCategoryField() {
        const categoryId = this.category?.value;
        if (categoryId >= 0) {
            // Recupero le stampanti associate alla categoria selezionata
            const printersInSelectedCategory = this.printersByCategory.get(categoryId);

            this.printersArray?.value.forEach((printer, index) => {
                const isPrinterEnabled = printersInSelectedCategory?.some(p => p.id === printer.id) || false;

                // Assegno il valore a _categoryEnabled
                this.getCategoryEnabledPrinter(index)?.setValue(isPrinterEnabled, {emitEvent: false});
                if(isPrinterEnabled) {
                    this.getEnabledPrinter(index)?.disable({emitEvent: false});
                    this.getEnabledPrinter(index)?.setValue(false, {emitEvent: false});
                }
            });
        }
    }

    getEnabledPrintersDescription() {
        let count = 0;
        this.printersArray?.value.forEach(printer => {
            if(printer._enabled || printer._categoryEnabled) count++;
        })
        const description = count === 1 ? this.translateService.instant('ITEMS.DETAILS.PRINTER_ENABLED') : this.translateService.instant('ITEMS.DETAILS.PRINTERS_ENABLED');
        return count + ' ' + description.toLowerCase();
    }


    getEnabledPrintersName() {
        const controls = this.printersArray?.controls || [];

        const names = controls
            .map(control => control.getRawValue())
            .filter(data => data._enabled || data._categoryEnabled)
            .map(data => data.name);

        if (names.length === 0) return '';

        return `(${names.join(', ')})`;
    }

    getGeneralFields(currentItem: Items) {
        // Recupero tutte le chiavi (nomi dei campi) presenti nel form
        const detailsFieldsToMap = Object.keys(this.detailsForm.controls);
        const priceFieldsToMap = Object.keys(this.pricesForm.controls);
        const costsFieldsToMap = Object.keys(this.costsForm.controls);
        const printersFieldsToMap = Object.keys(this.printersForm.controls);
        
        // Clono l'oggetto di destinazione per non mutare l'originale
        const updatedItem = { ...currentItem }; 

        // Ciclo su tutte le chiavi trovate nel form
        detailsFieldsToMap.forEach(key => {
            const control = this.detailsForm.get(key);
            
            if (control) {
                let value = control.value;

                if(typeof value === 'string') value = value || null;

                if(key === 'category') {
                    delete (updatedItem as any)[key];
                    key = 'category_id'
                    value = value || null
                }

                if((key === "default_supplier_id" || key === "color") && value == "") {
                    value = null;
                }

                // Assegno il valore all'oggetto.
                (updatedItem as any)[key] = value;
            }
        });

        priceFieldsToMap.forEach(key => {
            const control = this.pricesForm.get(key);
            
            if (control) {
                let value = control.value;

                if (key.startsWith('department')&& key.endsWith('_id')) {
                    value = (value === 0 || value === '0') ? null : value;
                }
                
                // Assegno il valore all'oggetto.
                (updatedItem as any)[key] = value
            }
        });

        costsFieldsToMap.forEach(key => {
            if (!GENERAL_FE_KEYS.includes(key)) {
                const control = this.costsForm.get(key);
                
                if (control) {
                    let value = control.value;
    
                    // Assegno il valore all'oggetto.
                    (updatedItem as any)[key] = value;
                }
            }
        });

        printersFieldsToMap.forEach(key => {
            const control = this.printersForm.get(key);

            if(control) {
                let value = control.value;

                if (key === 'printers' && Array.isArray(value)) {
                    value = value
                        // filtro solo le stampanti selezionate
                        ?.filter((printer: ItemPrintersFields) => printer._enabled) 
                        // costruisco l'oggetto
                        .map(({id}) => ({
                            id
                    }));
                }

                // Assegno il valore all'oggetto
                (updatedItem as any)[key] = value
            }
        })
        return updatedItem;
    }

    get detailsForm() {
        return this.itemGeneralForm?.controls.detailsForm;
    }

    get pricesForm() {
        return this.itemGeneralForm?.controls.pricesForm;
    }

    get costsForm() {
        return this.itemGeneralForm?.controls.costsForm;
    }

    get printersForm() {
        return this.itemGeneralForm?.controls.printersForm;
    }

    get on_sale() {
        return this.detailsForm?.controls.on_sale;
    }

    get favorite() {
        return this.detailsForm?.controls.favorite;
    }

    get name() {
        return this.detailsForm?.controls.name;
    }

    get alternativeNames() {
        return this.detailsForm?.controls.alternative_names;
    }

    get category() {
        return this.detailsForm?.controls.category;
    }

    get color() {
        return this.detailsForm?.controls.color;
    }

    get index() {
        return this.detailsForm?.controls.index;
    }

    get barcodes() {
        return this.detailsForm?.controls.barcodes;
    }

    get season() {
        return this.detailsForm?.controls.season;
    }

    get brand() {
        return this.detailsForm?.controls.brand;
    }

    get code_pienissimo() {
        return this.detailsForm?.controls.code_pienissimo;
    }

    get default_supplier_id() {
        return this.detailsForm?.controls.default_supplier_id;
    }

    get keyboard_code() {
        return this.detailsForm?.controls.keyboard_code;
    }

    get request_weighing() {
        return this.detailsForm?.controls.request_weighing;
    }

    get unit() {
        return this.detailsForm?.controls.unit;
    }

    get sku() {
        return this.detailsForm?.controls.sku;
    }

    get code() {
        return this.detailsForm?.controls.code;
    }

    get weight() {
        return this.detailsForm?.controls.weight;
    }

    get not_discountable() {
        return this.detailsForm?.controls.not_discountable;
    }

    getPrice(fieldName: string) {
        return this.pricesForm?.get(fieldName);
    }

    getDepartment(fieldName: string) {
        return this.pricesForm?.get(fieldName);
    }

    get cost() {
        return this.costsForm?.controls.cost;
    }

    get itemMargin() {
        return this.costsForm?.controls.itemMargin;
    }

    get itemMarkup() {
        return this.costsForm?.controls.itemMarkup;
    }

    get printersArray() {
        return this.printersForm?.controls.printers;
    }

    getIdPrinter(index: number) {
        return this.printersArray?.at(index).controls.id;
    }

    getNamePrinter(index: number) {
        return this.printersArray?.at(index).controls.name;
    }

    getEnabledPrinter(index: number) {
        return this.printersArray?.at(index).controls._enabled;
    }

    getCategoryEnabledPrinter(index: number) {
        return this.printersArray?.at(index).controls._categoryEnabled;
    }
}